import type {
  AchievementId,
  BoxDefId,
  ChapterId,
  FlagId,
  MiniGameId,
  NpcId,
  RoomId,
  TaskId,
  UnlockId,
} from '@/core/content/ids';
import type { ContentRegistry } from '@/core/content/registry';
import type { Condition } from '@/core/content/schemas/progression';
import type { GameSave } from '@/core/persistence/schema/v1';
import { cozinessLevelFor } from './coziness';
import { studioLevelFor } from './studioLevel';

/** Why something is still locked — rendered as "còn thiếu gì" (content-schemas §2.2). */
export type MissingReason =
  | { kind: 'stars'; have: number; need: number }
  | { kind: 'task'; taskId: TaskId }
  | { kind: 'unlock'; unlockId: UnlockId }
  | { kind: 'friendship'; npcId: NpcId; haveLevel: number; needLevel: number }
  | { kind: 'coziness'; roomId: RoomId; haveLevel: number; needLevel: number }
  | { kind: 'achievement'; achievementId: AchievementId }
  | { kind: 'coins'; amount: number; affordable: boolean }
  | { kind: 'flag'; flag: FlagId }
  | { kind: 'chapter'; chapterId: ChapterId }
  | { kind: 'box'; boxDefId: BoxDefId }
  | { kind: 'miniGame'; miniGameId: MiniGameId; level: number }
  | { kind: 'studio'; haveLevel: number; needLevel: number };

export type ConditionResult = { ok: true } | { ok: false; missing: MissingReason };

/** Pure read of one condition. `coinsCost` reports affordability but never counts as met automatically. */
export function evaluateCondition(content: ContentRegistry, s: GameSave, c: Condition): ConditionResult {
  const fail = (missing: MissingReason): ConditionResult => ({ ok: false, missing });
  switch (c.type) {
    case 'starsAtLeast':
      return s.stars.total >= c.value
        ? { ok: true }
        : fail({ kind: 'stars', have: s.stars.total, need: c.value });
    case 'taskCompleted': {
      const st = s.suggestions.tasks[c.taskId]?.status;
      return st === 'completed' || st === 'claimed' ? { ok: true } : fail({ kind: 'task', taskId: c.taskId });
    }
    case 'unlocked':
      return s.unlockedIds.includes(c.unlockId)
        ? { ok: true }
        : fail({ kind: 'unlock', unlockId: c.unlockId });
    case 'friendshipAtLeast': {
      const have = s.npcs[c.npcId]?.level ?? 0;
      return have >= c.level
        ? { ok: true }
        : fail({ kind: 'friendship', npcId: c.npcId, haveLevel: have, needLevel: c.level });
    }
    case 'cozinessAtLeast': {
      const have = cozinessLevelFor(content, c.roomId, s.coziness[c.roomId].best);
      return have >= c.level
        ? { ok: true }
        : fail({ kind: 'coziness', roomId: c.roomId, haveLevel: have, needLevel: c.level });
    }
    case 'achievementCompleted':
      return s.achievements[c.achievementId]?.completedAtPlayMs != null
        ? { ok: true }
        : fail({ kind: 'achievement', achievementId: c.achievementId });
    case 'coinsCost':
      return fail({ kind: 'coins', amount: c.amount, affordable: s.wallet.coins >= c.amount });
    case 'flag':
      return (s.flags[c.flag] ?? false) === c.value ? { ok: true } : fail({ kind: 'flag', flag: c.flag });
    case 'chapterSeen':
      return s.story.seenChapters.includes(c.chapterId)
        ? { ok: true }
        : fail({ kind: 'chapter', chapterId: c.chapterId });
    case 'boxOpened':
      return Object.values(s.inventory.boxes).some((b) => b.boxDefId === c.boxDefId && b.opened)
        ? { ok: true }
        : fail({ kind: 'box', boxDefId: c.boxDefId });
    case 'miniGameCleared':
      return s.miniGames[c.miniGameId]?.levelsCleared.includes(c.level) === true
        ? { ok: true }
        : fail({ kind: 'miniGame', miniGameId: c.miniGameId, level: c.level });
    case 'studioLevelAtLeast': {
      const have = studioLevelFor(content, s.studio.reputation);
      return have >= c.level ? { ok: true } : fail({ kind: 'studio', haveLevel: have, needLevel: c.level });
    }
  }
}

/** AND over a list; returns every missing reason (for UI), coinsCost excluded unless `includeCost`. */
export function evaluateAll(
  content: ContentRegistry,
  s: GameSave,
  conditions: readonly Condition[],
  includeCost = false,
): { ok: boolean; missing: MissingReason[] } {
  const missing: MissingReason[] = [];
  for (const c of conditions) {
    if (c.type === 'coinsCost' && !includeCost) continue;
    const r = evaluateCondition(content, s, c);
    if (!r.ok) missing.push(r.missing);
  }
  return { ok: missing.length === 0, missing };
}
